import type {
  ProcessMachineMapDocument,
  ProcessMachineNode,
  ProcessMachineNodeType,
} from '../types/processMachineMap';

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

const KNOWN_TYPES = new Set<ProcessMachineNodeType>([
  'fab',
  'process',
  'subprocess',
  'tool_group',
  'machine',
]);

/** Map backend / LLM variants onto the mindmap node kinds. */
export function normalizeProcessMachineNodeType(
  raw: unknown,
  hints?: { hasLocation?: boolean; childTypes?: string[] }
): ProcessMachineNodeType {
  const text =
    typeof raw === 'string'
      ? raw
      : isRecord(raw) && typeof raw.name === 'string'
        ? raw.name
        : '';

  const key = text
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_')
    .replace(/__/g, '_');

  const aliases: Record<string, ProcessMachineNodeType> = {
    fab: 'fab',
    root: 'fab',
    factory: 'fab',
    plant: 'fab',
    site: 'fab',
    floor_plan: 'fab',
    floorplan: 'fab',
    process: 'process',
    processes: 'process',
    process_area: 'process',
    process_group: 'process',
    subprocess: 'subprocess',
    sub_process: 'subprocess',
    subprocesses: 'subprocess',
    tool_group: 'tool_group',
    toolgroup: 'tool_group',
    tool: 'tool_group',
    tools: 'tool_group',
    group: 'tool_group',
    equipment_group: 'tool_group',
    tool_type: 'tool_group',
    machine: 'machine',
    machines: 'machine',
    equipment: 'machine',
    tool_instance: 'machine',
    instance: 'machine',
  };

  if (aliases[key]) return aliases[key];
  if (KNOWN_TYPES.has(key as ProcessMachineNodeType)) {
    return key as ProcessMachineNodeType;
  }

  // Infer from payload shape when the API uses an unexpected label.
  if (hints?.hasLocation) return 'machine';
  const childTypes = hints?.childTypes ?? [];
  if (childTypes.some((t) => t === 'machine' || t === 'equipment' || t === 'tool_instance')) {
    return 'tool_group';
  }
  if (
    childTypes.some(
      (t) =>
        t === 'tool_group' ||
        t === 'toolgroup' ||
        t === 'group' ||
        t === 'subprocess' ||
        t === 'sub_process'
    )
  ) {
    return 'process';
  }
  if (childTypes.some((t) => t === 'process' || t === 'tool_group' || t === 'machine')) {
    return 'fab';
  }

  return 'process';
}

function readNodeName(raw: Record<string, unknown>): string | null {
  const candidates = [raw.name, raw.label, raw.title, raw.display_name, raw.displayName];
  for (const c of candidates) {
    if (typeof c === 'string' && c.trim()) return c.trim();
  }
  return null;
}

function readNodeId(raw: Record<string, unknown>, fallbackName: string, path: string): string {
  const candidates = [raw.id, raw.node_id, raw.nodeId, raw.uuid, raw.key];
  for (const c of candidates) {
    if (typeof c === 'string' && c.trim()) return c.trim();
    if (typeof c === 'number' && Number.isFinite(c)) return String(c);
  }
  return `${path}:${fallbackName}`;
}

function readLocation(raw: Record<string, unknown>): [number, number] | null | undefined {
  const loc = raw.location;
  if (loc == null) return loc === null ? null : undefined;
  if (Array.isArray(loc) && loc.length >= 2) {
    const x = Number(loc[0]);
    const y = Number(loc[1]);
    if (Number.isFinite(x) && Number.isFinite(y)) return [x, y];
  }
  if (isRecord(loc)) {
    const x = Number(loc.x ?? loc[0]);
    const y = Number(loc.y ?? loc[1]);
    if (Number.isFinite(x) && Number.isFinite(y)) return [x, y];
  }
  return undefined;
}

function readChildren(raw: Record<string, unknown>): unknown[] {
  const candidates = [
    raw.children,
    raw.nodes,
    raw.items,
    raw.processes,
    raw.tool_groups,
    raw.toolGroups,
    raw.machines,
  ];
  for (const c of candidates) {
    if (Array.isArray(c)) return c;
  }
  return [];
}

function normalizeNode(value: unknown, path: string): ProcessMachineNode | null {
  if (!isRecord(value)) return null;

  const name = readNodeName(value);
  if (!name) return null;

  const childRaw = readChildren(value);
  const childTypes = childRaw
    .filter(isRecord)
    .map((c) => (typeof c.type === 'string' ? c.type : typeof c.node_type === 'string' ? c.node_type : ''))
    .filter(Boolean);

  const location = readLocation(value);
  const rawType = value.type ?? value.node_type ?? value.nodeType ?? value.kind ?? value.level;
  const type = normalizeProcessMachineNodeType(rawType, {
    hasLocation: Array.isArray(location),
    childTypes,
  });

  const children = childRaw
    .map((child, index) => normalizeNode(child, `${path}/${index}`))
    .filter((n): n is ProcessMachineNode => !!n);

  const node: ProcessMachineNode = {
    id: readNodeId(value, name, path),
    name,
    type,
  };

  if (typeof value.code === 'string') node.code = value.code;
  if (typeof value.count === 'number') node.count = value.count;
  if (typeof value.tool_name === 'string' || value.tool_name === null) {
    node.tool_name = value.tool_name as string | null;
  }
  if (typeof value.tool_number === 'string' || value.tool_number === null) {
    node.tool_number = value.tool_number as string | null;
  }
  if (typeof value.equipment_type === 'string' || value.equipment_type === null) {
    node.equipment_type = value.equipment_type as string | null;
  }
  if (typeof value.work_cell === 'string' || value.work_cell === null) {
    node.work_cell = value.work_cell as string | null;
  }
  if (typeof value.layer === 'string' || value.layer === null) {
    node.layer = value.layer as string | null;
  }
  if (typeof value.confidence === 'string' || value.confidence === null) {
    node.confidence = value.confidence as string | null;
  }
  if (location !== undefined) node.location = location;
  if (children.length > 0) node.children = children;

  return node;
}

function findRootCandidate(payload: Record<string, unknown>): unknown {
  if (payload.root) return payload.root;
  if (payload.tree) return payload.tree;
  if (payload.map) return payload.map;
  if (isRecord(payload.data)) {
    if (payload.data.root) return payload.data.root;
    if (payload.data.tree) return payload.data.tree;
  }
  if (isRecord(payload.result) && payload.result.root) return payload.result.root;
  // Some APIs return the root node at the top level.
  if (typeof payload.id === 'string' || typeof payload.name === 'string') {
    return payload;
  }
  return null;
}

/** Validate and normalize an uploaded / API process–machine map JSON payload. */
export function parseProcessMachineMapDocument(payload: unknown): ProcessMachineMapDocument {
  if (!isRecord(payload)) {
    throw new Error('JSON must be an object with a root tree.');
  }

  const rootRaw = findRootCandidate(payload);
  const root = normalizeNode(rootRaw, 'root');
  if (!root) {
    throw new Error('Missing or invalid "root" tree (expected id/name and children).');
  }

  // Ensure top-level is treated as fab when the API labels it "root".
  if (root.type !== 'fab' && (!root.children || root.children.every((c) => c.type !== 'fab'))) {
    const looksLikeFab =
      root.children?.some(
        (c) => c.type === 'process' || c.type === 'subprocess' || c.type === 'tool_group'
      ) ?? false;
    if (looksLikeFab) root.type = 'fab';
  }

  const summaryRaw = isRecord(payload.summary)
    ? payload.summary
    : isRecord(payload.data) && isRecord(payload.data.summary)
      ? payload.data.summary
      : {};

  const summary = {
    total_machines: Number(summaryRaw.total_machines) || 0,
    total_processes: Number(summaryRaw.total_processes) || 0,
    total_tool_groups: Number(summaryRaw.total_tool_groups) || 0,
  };

  return {
    floor_plan_id:
      typeof payload.floor_plan_id === 'string'
        ? payload.floor_plan_id
        : typeof payload.floorPlanId === 'string'
          ? payload.floorPlanId
          : '',
    name:
      typeof payload.name === 'string'
        ? payload.name
        : typeof payload.title === 'string'
          ? payload.title
          : 'Uploaded process map',
    summary,
    root,
  };
}

export async function readProcessMachineMapFile(file: File): Promise<ProcessMachineMapDocument> {
  const text = await file.text();
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    throw new Error('File is not valid JSON.');
  }
  return parseProcessMachineMapDocument(parsed);
}
